import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { ArrowRight, Check } from 'lucide-react'
import { DARK, TEAL, BG, MarketingNavBar, MarketingFooter, FaqAccordion, faqJsonLd } from '../../components/MarketingLayout'

const FAQS = [
  {
    q: 'Akkor a "HACCP igazolás" hivatalos dokumentum neve?',
    a: 'Nem. A HACCP igazolás nem hivatalos, jogszabályban rögzített elnevezés — ez egy köznyelvi gyűjtőfogalom, amivel a megrendelők a "van-e élelmiszerbiztonsági papírod" kérdést teszik fel. A jogszabály a HACCP-alapú rendszer működtetését (852/2004/EK) és a nyomonkövethetőséget írja elő, nem egy konkrét "HACCP igazolás" nevű nyomtatványt.',
  },
  {
    q: 'Mit adjak oda, ha egy étterem vagy óvoda HACCP igazolást kér egy szállítmányhoz?',
    a: 'Egy eredetigazolást (más néven származási igazolást): terméknév, összetétel, allergének, tárolási előírás, a te céged adatai, a kiállítás dátuma és aláírás/pecsét helye. Ez szinte mindig ki fogja elégíteni a kérést.',
  },
  {
    q: 'Mikor kérhetik ténylegesen a teljes HACCP dokumentációt?',
    a: 'Hatósági ellenőrzéskor (NÉBIH), auditnál, vagy ha egy nagyobb megrendelő (pl. áruházlánc, közétkeztetési beszállítói szerződés) kifejezetten a teljes rendszerdokumentációt kéri bemutatásra — ilyenkor a veszélyelemzésre, a CCP-kre és a napi naplókra van szükség, nem egy egyszeri papírra.',
  },
]

export default function MitJelentAHaccpIgazolasPage() {
  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <Helmet>
        <title>Mit jelent valójában a "HACCP igazolás"? — CukrászDoc</title>
        <meta name="description" content="Amikor egy étterem vagy óvoda HACCP igazolást kér, a legtöbbször nem a teljes HACCP-rendszerre gondol. Elmagyarázzuk, mit kérnek valójában, és minek kell benne lennie." />
        <link rel="canonical" href="https://cukraszdoc.hu/blog/mit-jelent-a-haccp-igazolas" />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd(FAQS))}</script>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: 'Mit jelent valójában a "HACCP igazolás"?',
          datePublished: '2026-07-29',
          dateModified: '2026-07-29',
          author: { '@type': 'Organization', name: 'CukrászDoc' },
          publisher: { '@type': 'Organization', name: 'CukrászDoc' },
        })}</script>
      </Helmet>
      <MarketingNavBar />

      <section style={{ background: `linear-gradient(160deg, ${DARK} 0%, #162119 100%)`, paddingTop: '5rem' }} className="relative overflow-hidden">
        <div className="relative max-w-3xl mx-auto px-6 py-16 lg:py-20">
          <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full text-xs font-semibold"
            style={{ background: 'rgba(91,184,160,0.15)', color: TEAL, border: '1px solid rgba(91,184,160,0.3)' }}>
            Tudástár
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold leading-tight text-white mb-5">
            Mit jelent valójában a <span style={{ color: TEAL }}>"HACCP igazolás"</span>?
          </h1>
          <p className="text-lg leading-relaxed" style={{ color: 'rgba(255,255,255,0.6)' }}>
            Éttermek, óvodák, iskolák szinte mindig ezt a szót használják — de a legtöbbször nem ugyanarra gondolnak. Itt a rövid magyarázat, mit kérnek valójában, és mit adj nekik.
          </p>
        </div>
        <div className="relative" style={{ height: 60, marginBottom: -2 }}>
          <svg viewBox="0 0 1440 60" preserveAspectRatio="none" className="absolute bottom-0 w-full h-full">
            <path d="M0,30 C360,60 1080,0 1440,30 L1440,60 L0,60 Z" fill="#ffffff" />
          </svg>
        </div>
      </section>

      <article className="bg-white py-16">
        <div className="max-w-3xl mx-auto px-6 flex flex-col gap-10">

          <div className="rounded-2xl p-6" style={{ background: BG, border: '1px solid rgba(91,184,160,0.2)' }}>
            <p className="text-base leading-relaxed" style={{ color: DARK }}>
              <strong>Röviden:</strong> amikor egy étterem vagy óvoda "HACCP igazolást" kér egy adott szállítmányhoz, a gyakorlatban egy <strong>eredetigazolást</strong> (más néven származási igazolást) akar — nem a teljes HACCP-rendszered dokumentációját. A "HACCP igazolás" nem hivatalos dokumentumnév, hanem köznyelvi gyűjtőfogalom mindenre, ami az élelmiszerbiztonsággal kapcsolatos papírmunkát jelenti.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold mb-4" style={{ color: DARK }}>Miért nevez mindenki mindent "HACCP igazolásnak"?</h2>
            <p className="text-base leading-relaxed text-gray-600">
              A HACCP a magyar vendéglátásban és élelmiszeriparban annyira egyet jelent az "élelmiszerbiztonsággal", hogy a hétköznapokban ez a szó lett a gyűjtőfogalom minden ide vonatkozó papírra — függetlenül attól, hogy jogilag pontosan miről is van szó. Egy étteremvezető vagy óvodai konyhavezető nem azt akarja tudni, melyik jogszabály melyik dokumentumot írja elő; azt akarja tudni, hogy "biztonságos-e, amit veszek, és van-e hozzá papír". Ez a kérdés bújik meg a "kérünk HACCP igazolást" mondat mögött.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold mb-4" style={{ color: DARK }}>Két különböző dolog, amit ezzel a szóval kérhetnek</h2>
            <div className="flex flex-col gap-4 mt-2">
              <div className="rounded-2xl p-6" style={{ border: '1px solid rgba(91,184,160,0.15)' }}>
                <h3 className="font-semibold text-base mb-2" style={{ color: DARK }}>1. Egy konkrét szállítmányhoz kért papír <span className="font-normal text-gray-400">— ez a leggyakoribb</span></h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Egy óvoda, iskola vagy étterem, amikor átveszi a tortát/süteményt, azt szeretné látni: ki készítette, hol, milyen összetevőkből, milyen allergénekkel, és mikor. Ez az <Link to="/szarmazasi-igazolas" style={{ color: TEAL, fontWeight: 600 }}>eredetigazolás</Link> — ezt kérik "HACCP igazolásként" a legtöbbször.
                </p>
              </div>
              <div className="rounded-2xl p-6" style={{ border: '1px solid rgba(91,184,160,0.15)' }}>
                <h3 className="font-semibold text-base mb-2" style={{ color: DARK }}>2. A teljes HACCP-rendszer dokumentációja <span className="font-normal text-gray-400">— hatósági ellenőrzéskor, auditnál</span></h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Ez a <Link to="/haccp-igazolas" style={{ color: TEAL, fontWeight: 600 }}>teljes HACCP dokumentáció</Link>: veszélyelemzés, kritikus szabályozási pontok (CCP), monitoring rendszer és a napi vezetett napló. Ezt jellemzően a NÉBIH vagy egy auditor kéri, nem egy vevő egy adott szállítmányhoz.
                </p>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-bold mb-4" style={{ color: DARK }}>Mit tartalmazzon a papír, amit a vevőnek adsz?</h2>
            <div className="rounded-2xl p-6" style={{ background: BG, border: '1px solid rgba(91,184,160,0.2)' }}>
              <ul className="flex flex-col gap-3">
                {[
                  'Termék neve és összetétele (receptúra alapján)',
                  'Allergénjelölés (a 14 hivatalos EU-allergén közül, ami releváns)',
                  'Tárolási előírás',
                  'A te céged neve, székhelye, telephelye, kapcsolattartó',
                  'A kiállítás dátuma',
                  'Aláírás / pecsét helye',
                ].map(item => (
                  <li key={item} className="flex items-start gap-3">
                    <Check size={18} style={{ color: TEAL }} className="mt-0.5 shrink-0" />
                    <span className="text-sm text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div>
            <p className="text-base leading-relaxed text-gray-600">
              A gyakorlatban ez az adat ugyanabból a helyről jön, ahonnan a <Link to="/gyartmanylap" style={{ color: TEAL, fontWeight: 600 }}>gyártmánylapod</Link> és az <Link to="/allergen-cimke-nyomtatas" style={{ color: TEAL, fontWeight: 600 }}>allergén jelölésed</Link> is — egyszer viszed fel a receptet, utána mindkét dokumentumtípus (a vevőnek szóló papír és a hatóságnak szóló teljes dokumentáció) ugyanabból generálódik.
            </p>
          </div>

        </div>
      </article>

      <section style={{ background: BG }} className="py-20">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-10" style={{ color: DARK }}>Gyakori kérdések</h2>
          <FaqAccordion items={FAQS} />
        </div>
      </section>

      <section style={{ background: DARK }} className="py-20">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ne kelljen legközelebb utánagondolnod</h2>
          <p className="mb-8 text-base" style={{ color: 'rgba(255,255,255,0.55)' }}>A CukrászDoc-ban a recept felvitele után mindkét dokumentumtípus egy kattintással elkészül.</p>
          <Link to="/register" className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold text-base" style={{ background: TEAL, color: '#fff' }}>
            Ingyenes regisztráció <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <MarketingFooter />
    </div>
  )
}
